// @ts-strict-ignore
import { Injectable } from '@angular/core';
import { LMContextService } from '../lm-context.service';
import {
  ActionResource,
  FormResource,
  Resource,
  KeyedResource,
} from '@resources/index';

import * as urltemplate from 'url-template';
import { isEmpty } from 'lodash-es';
import { isPlainObject } from 'lodash-es';

import { SohoMessageService } from 'ids-enterprise-ng';
import { AppConstants, LMBrowserUtilService, LMDialogButtonKey } from '@utils/index';

export interface URLOptions {
  resource: Resource;
  contextResource?: Resource;
  params?: any;
  userFolderId?: string;
  webApp?: string;
}

export interface NameValueMap {
  [key: string]: string
}

@Injectable()
export class LMUrlUtilService {

  private accessibleWebAppsUrlTemplate: string;
  private bannerSpecUrlTemplate: string;
  private signOutUrlTemplate: string;

  constructor(
    private sohoMessageService: SohoMessageService,
    private lmBrowserUtil: LMBrowserUtilService,
    private lmContextService: LMContextService
  ) {}

  getUrlParam(url: string, param: string): any | null {
    if (url) {
      const urlParam = this.getUrlParams(url)[param];
      if (!urlParam) {
        return null;
      }

      return urlParam;
    }

    return null;
  }

  /**
   * useful to remove the hash and all chars to its right on the url.
   * @param url
   */
  public removeHash(url: string): string {
    // perhaps overkill but being super safe here to avoid javascript errors
    if (!url || url.indexOf('#') === -1) {
      return url;
    }

    return url.split('#')[0];
  }

  public getUrlPath(url: string): string {
    url = this.toRelativeUrl(url);
    const findPos = url.indexOf('?');
    if (findPos >= 0) {
      return url.substring(0, findPos);
    }
    return url;
  }

  // /**
  //  * Potentially more generic solution for
  //  * https://inforwiki.atlassian.net/browse/LMWEB-21344.
  //  * The url would have to have all resourceId keys encoded
  //  * in case they contain a hash symbol
  //  */
  // public getUrlPath(url: string, ensureNoFragment = false): string {
  //   url = this.toRelativeUrl(url);
  //   const findPos = url.indexOf('?');
  //   if (findPos >= 0) {
  //     return url.substring(0, findPos);
  //   } else if (ensureNoFragment) {
  //     const hashIndex = url.indexOf('#');
  //     if (hashIndex >= 0) {
  //       return url.substring(0, hashIndex);
  //     }
  //   }
  //   return url;
  // }

  updateContextSessionKeyParams(url: string): string {
    const urlParams: LMUrlParams = {};
    const context = this.lmContextService.getContext();

    if (!context.contextSessionKeys || jQuery.isEmptyObject(context.contextSessionKeys)) {
      return url;
    }

    Object.keys(context.contextSessionKeys).forEach(key => {
      urlParams[ key ] = encodeURIComponent(decodeURIComponent(context.contextSessionKeys[ key ]));
    });

    return this.updateUrlParams(url, urlParams);
  }

  /**
   * Helper method to update, add, or remove a parameter in a url
   *
   * @param url The url to find and replace a parameter value
   * @param urlParams The urlParams to replace
   * @returns A new url with the updated params.
   */
  updateUrlParams(url: string, urlParams: LMUrlParams): string {

    if (!urlParams) {
      return url;
    }

    const keys: Array<string> = Object.keys(urlParams);
    for (let i = 0, len = keys.length; i < len; i++) {
      const key = keys[ i ];
      const value = urlParams[ key ];
      let expression: RegExp;
      let removeParam = false;

      switch (typeof urlParams[ key ]) {
        case 'number':
          // match number value, negative numbers are valid so match '-' char as well
          expression = new RegExp(key + '=[-0-9]*');
          break;

        case 'boolean':
          // use parens to match a full string. use | to delimate between different strings.
          // for example: (true|false) says match the entire string of true or false.
          expression = new RegExp(key + '=(true|false)');
          break;

        case 'string':
          if (value.trim().length === 0) {
            removeParam = true;
            break;
          }

          /**
           * Match any character between the key= and the '&' or end-of-string.
           * -----------------------------------------------------------------------------------------------------------
           * Explanation - https://regex101.com/r/qyRGGn/2
           * -----------------------------------------------------------------------------------------------------------
           * / pageop=.+?(?=[&\z]) / g
           *   pageop= matches the characters pageop= literally (case sensitive)
           *
           * - Match a single character not present in the list below
           *   [^&?]*
           *   ^ starts the match after the <b>key + '='</b> sign.
           *   * Quantifier — Matches between zero and unlimited times, as many times as possible,
           *     giving back as needed (greedy)
           *   &? matches a single character in the list &? (case sensitive)
           * - Global pattern flags
           *   g modifier: global. All matches (don't return after first match)
           * -----------------------------------------------------------------------------------------------------------
           */
          expression = new RegExp(key + '=[^&#?]*');
          break;

        case 'object':    // in case null is used
        case 'undefined': // in case value is undefined
          if (!value) {
            removeParam = true;
          }
          break;

        default:
          continue;
      }

      if (removeParam) {
        url = this.removeParam(url, key);
      } else if (url.match(expression)) {
        // if expression is matched then replace with the new value.
        url = url.replace(expression, key + '=' + value);
      } else {
        // if expression is not found then append the key value pair to the url
        // const delimiter: string = url.indexOf('?') === -1 ? '?' : '&';
        // url += delimiter + key + '=' + value;

        // ----------------------------------------------------
        // when appending a url param we need to take a hash
        // into account. If there's a hash the url param must
        // placed before the hash.
        // todo: is there a better regex way to do this?
        // ----------------------------------------------------
        const urlParts = url.split('#');
        let tmpUrl = urlParts[0];
        const delimiter: string = tmpUrl.indexOf('?') === -1 ? '?' : '&';
        tmpUrl += delimiter + key + '=' + value;
        tmpUrl += urlParts.length === 2 ? '#' + urlParts[1]: '';
        url = tmpUrl;
      }
    }

    return url;
  }

  // buildRelatedUrl(urlTemplate: string, fromResourceId: string): string {
  //   const compiledUrlTemplate = urltemplate.parse(urlTemplate);
  //   return compiledUrlTemplate.expand({ resourceId: fromResourceId });
  // }
  //
  // buildRelatedUrl(initialRelatedData: LMInitialRelatedData, fromDataView: LMDataView, url?: string) {
  //   let fromResource: KeyedResource = this.buildContextResourceId(initialRelatedData, fromDataView);
  //   let baseUrl = url || initialRelatedData.baseUrl;
  //   return baseUrl + '&' + fromResource.resourceType + '=' + fromResource.resourceId;
  // }
  //
  // /**
  //  * Builds a context resource object for a given dataView.
  //  * todo: update to handler multiple contextResources listed in listSpec.initialRelatedData object.
  //  * @param listSpec The list spec of the related list containing the sourceResourceId and sourceResourceType
  //  * @param fromDataView The dataView to be used to resolve the context resourceId template.
  //  * @returns {KeyedResource} A keyed resource resolved against the dataview.
  //  */
  // buildContextResourceId(initialRelatedData: LMInitialRelatedData, fromDataView: LMDataView): KeyedResource {
  //   let viewKeyResource = new KeyedResource({ resourceId: fromDataView.resourceId });
  //   let fromResource = new KeyedResource({
  //     resourceId: initialRelatedData.contextResources[0].resourceId,
  //     resourceType: initialRelatedData.contextResources[0].resourceType,
  //     contextValueArray: viewKeyResource.contextValueArray,
  //     dataView: fromDataView
  //   });
  //
  //   return fromResource;
  // }

  public getUrlParams(url: string): Params {
    const urlParams: Params = {};
    let urlParamStr = '';

    if (url) {
      url = this.removeHash(url);
      const paramPos = url.indexOf('?');
      if (paramPos < 0) {
        return urlParams;
      }
      urlParamStr = url.substring(paramPos + 1);

      const paramArray = urlParamStr.split('&');
      paramArray.forEach((param: string) => {
        const keyValue = param.split('=');

        // 'item' and 'fld' can appear as url params multiple times.
        // If they do then convert the url param value to an array that
        // includes each value in the array.
        // example: item = [item1, item2, item3];
        //
        // LMUrlUtilService.buildUrl2 will unpack this array and place
        // multiple item url params on the url.
        const key = keyValue[0];
        if (urlParams[key]) {
          if (!(urlParams[key] instanceof Array)) {
            urlParams[key] = [ urlParams[key] ];
          }
          urlParams[key] = [ ...urlParams[key], keyValue[ 1 ] ];
        } else {
          urlParams[ keyValue[ 0 ] ] = keyValue[ 1 ];
        }
      });
    }
    return urlParams;
  }

  public objectToCubeQueryString(params?: any): string {
    if (!params || isEmpty(params) || !isPlainObject(params)) {
      return null;
    }
    const queryString1 = Object.keys(params)
      .filter(k => !(Array.isArray(params[ k ]) || isPlainObject(params[ k ])))
      .map((l) => encodeURIComponent(l) + '=' + encodeURIComponent(params[ l ]))
      .join('&');

    // Array of Array of objects.
    const queryString2 = Object.keys(params)
      .filter(k => Array.isArray(params[ k ]))
      .map(l => params[ l ])
      .map(m => m.map((n, index) => Object.keys(n)
        .map(o => encodeURIComponent(o) + index + '=' + encodeURIComponent(n[ o ]))
        .join('&'))
        .join('&'))
      .join('&');

    return queryString1 + '&' + queryString2;
  }

  public cubeOverridesToQueryString(cubeOverrides: LMCubeOverrides, delimiter = '&'): string {
    let params = '';
    const dimOverrides = this.objectToQueryString(cubeOverrides.dimensionOverrides);
    if (dimOverrides !== null) {
      params += delimiter + dimOverrides;
      delimiter = '&';
    }

    if (cubeOverrides.reportingBasis) {
      params += delimiter + 'reportingBasis=' + cubeOverrides.reportingBasis;
      delimiter = '&';
    }

    if (cubeOverrides.topDimensions?.length) {
      const topDimensions = this.objectToCubeQueryString({ topDimensions: cubeOverrides.topDimensions });
      if (topDimensions !== null) {
        params += topDimensions;
        delimiter = '&';
      }
    }

    if (cubeOverrides.fltrDimensions?.length) {
      const fltrDimensions = this.objectToCubeQueryString({ fltrDimensions: cubeOverrides.fltrDimensions });
      if (fltrDimensions !== null) {
        params += fltrDimensions;
        delimiter = '&';
      }
    }

    if (cubeOverrides.topContextFields?.length) {
      const topContextFields = this.objectToCubeQueryString({ topContextFields: cubeOverrides.topContextFields });
      if (topContextFields !== null) {
        params += topContextFields;
        delimiter = '&';
      }
    }

    const periodOverrides = this.objectToQueryString(cubeOverrides.periodOverrides);
    if (periodOverrides !== null) {
      params += delimiter + periodOverrides;
      delimiter = '&';
    }

    if (cubeOverrides.periodView) {
      params += delimiter + 'periodView=' + cubeOverrides.periodView;
      delimiter = '&';
    }

    if (cubeOverrides.pivotView) {
      params += delimiter + 'pivot=' + cubeOverrides.pivotView;
      delimiter = '&';
    }

    if (cubeOverrides.columnView) {
      params += delimiter + 'colView=' + cubeOverrides.columnView;
      delimiter = '&';
    }
    return params;
  }

  public addParamToUrl(url: string, paramIn: string, valueIn: string): string {
    if (url.indexOf(paramIn) >= 0) {
      const urlParams: LMUrlParams = {};
      urlParams[paramIn] = valueIn;

      return this.updateUrlParams(url, urlParams);
    } else {
      return this.appendToQueryString(url, paramIn + '=' + valueIn);
    }
  }

  public appendToQueryString(url: string, parameter: string): string {
    const delimiter = (url.indexOf('?') > -1) ? '&' : '?';
    return url + delimiter + parameter;
  }

  public objectToQueryString(params?: any): string {
    if (!params || isEmpty(params) || !isPlainObject(params)) {
      return null;
    }

    // todo spec-url-refactor is encoding the key really required?
    //   see usage where navigationContext is passed in called.
    return Object.keys(params)
      .map((k) => encodeURIComponent(k) + '=' + encodeURIComponent(params[ k ]))
      .join('&');
  }

  public buildFormTransactionUrlOptions(resourceId: string): URLOptions {
    const params: any = { action: '_execute' };
    const resource: FormResource = new FormResource({
      resourceType: 'form',
      resourceId
    });

    const options: URLOptions = {
      resource,
      params,
    };

    return options;
  }

  public buildRefreshColumnarDataUrlOptions(): URLOptions {
    const params: any = { action: 'RefreshColumnarData' };
    const resource: FormResource = new FormResource({
      resourceType: 'form',
      resourceId: 'RefreshColumnarData.RefreshColumnarDataForm'
    });

    const options: URLOptions = {
      resource,
      params,
    };

    return options;
  }

  public buildFormActionRedirectUrlOptions(dataView: LMDataView, actionSpec: LMActionSpec): URLOptions {
    // Resource is Action Resource and action controller urls requires output = spec at the end
    const params: any = { form: dataView.resourceId, output: 'spec' };
    const resource: ActionResource = new ActionResource(
      {
        resourceType: 'action',
        resourceId: dataView.resourceId,
        actionName: actionSpec.name
      });

    const options: URLOptions = {
      resource,
      params,
    };

    return options;
  }

  public buildUrl(urlOptions: URLOptions) {
    const resource = urlOptions.resource;
    const contextResource = urlOptions.contextResource;
    const params = urlOptions.params;
    const userFolderId = urlOptions.userFolderId;
    const webapp = urlOptions.webApp;

    // encodeURI wansn't encoding '+' plus signs, so use encodeURIComponent instead
    // http://jira.lawson.com/browse/LMCLIENT-3878
    let url = this.buildBaseUrl(webapp) + '/' + resource.resourceType + '/' + resource.generateResourceId(true);
    let delimiter = '?';

    if (contextResource) {
      url += delimiter + contextResource.resourceType + '=' + contextResource.generateResourceId(true);
      delimiter = '&';
    }

    if (userFolderId) {
      url += delimiter + 'userfolder=' + userFolderId;
      delimiter = '&';
    }

    if (params && !isEmpty(params) && isPlainObject(params)) {
      url += delimiter + this.objectToQueryString(params);
      delimiter = '&';
    }

    url = this.updateContextSessionKeyParams(url);

    const context = this.lmContextService.getContext();
    if (context.proxy) {
      url += delimiter + '_u=' + context.proxy;
    }

    return this.encodeParens(url);
  }

  public fieldsToUrlParams(harnessFields: LMHarnessInterfaceFields, paramPrefix: string, initialQuestionMark?: boolean): string {
    // The method was written wrong from the start and did not properly process the LMHarnessInterfaceFields definition.
    // To maintain backwards compatibility we check the object to see what type it is and process it to the correct results.
    let strParams = '';
    if (harnessFields) {
      if(harnessFields.fields && Array.isArray(harnessFields.fields)) {
        harnessFields.fields.map((field, index) => {
          const delimiter = (index === 0 && initialQuestionMark) ? '?' : '&';
          strParams += delimiter + paramPrefix + '.' + field.field + '=' + field.value;
        });
      } else {
        Object.keys(harnessFields).map((fieldId, index) => {
          const delimiter = (index === 0 && initialQuestionMark) ? '?' : '&';
          strParams += delimiter + paramPrefix + '.' + fieldId + '=' + harnessFields[fieldId].value;
        });
      }
    }

    return strParams;
  }

  public propagateSessionKeys(url: string, delimiter: string): string {
    const context = this.lmContextService.getContext();
    if (context.contextSessionKeys && Object.keys(context.contextSessionKeys).length !== 0) {
      const urlParams = this.getParams(url);
      Object.keys(context.contextSessionKeys).forEach((key) => {
        if (!urlParams.hasOwnProperty(key)) {
          url += delimiter + key + '=' + encodeURIComponent(context.contextSessionKeys[ key ]);
          delimiter = '&';
        }
      });
    }
    return url;
  }

  /**
   * todo maria, we really don't want to have to encode parens. Angular, however, is failing unless we do.
   * Encodes parens due to angular being unable to handle them.
   * @param url
   * @returns
   */
  public encodeParens(url: string) {
    return url
      .replace(/\(/g, '%28')
      .replace(/\)/g, '%29');
  }

  public encodePlus(url: string): string {
    let queryStringIndex = url.indexOf('?');
    if (queryStringIndex === -1) {
      queryStringIndex = url.length;
    }

    const uri = url.substring(0, queryStringIndex);
    const openParenIndex = uri.indexOf('(');
    const closeParenIndex = uri.lastIndexOf(')');
    if (openParenIndex === -1 || closeParenIndex === -1  || openParenIndex > queryStringIndex) {
      return url;
    }

    const resource = uri.substring(openParenIndex + 1, closeParenIndex).replace(/\+/g, '%20');
    return url.substr(0, openParenIndex + 1) + resource + url.substr(closeParenIndex);
  }

  public encodeHtmlEntity(text: string): string {
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  public getBaseUrl(relativeUrl?: boolean): string {
    return this.buildBaseUrl(undefined, relativeUrl);
  }
  public buildBaseUrl(altWebapp: string, relativeUrl?: boolean): string {
    const context = this.lmContextService.getContext();

    if (context) {
      const urlPdlOrDa = context.urlPdlOrDa;
      const webApp = altWebapp ? altWebapp : context.webApp;
      const isGen = this.lmContextService.isGenProductLine();

      if (webApp && (urlPdlOrDa || isGen)) {
        if (isGen) {
          if (relativeUrl) {
            return '/' + webApp;
          } else {
            return location.protocol + '//' + location.host + '/' + webApp;
          }
        } else {
          if (relativeUrl) {
            return '/' + urlPdlOrDa + '/' + webApp;
          } else {
            return location.protocol + '//' + location.host + '/' + urlPdlOrDa + '/' + webApp;
          }
        }
      }
    }

    // todo pwpatton this should never happen.
    console.log('ERROR: LMUrlUtilService:getBaseUrl(): url should never be relative, please refactor calling code');
    return '..';
  }

  /**
   * Examples:
   *   { modifier: 'contains', fieldId: 'JobPostingSearchRel_col_KeywordSearch', value: 'St. Paul' }
   *   fc.JobPostingSearchRel_col_KeywordSearch=contains(St.+Paul)
   *
   * @param searchCriteria
   * @returns
   */
  public filterFieldCriteriaToUrlParams(filterCriteriaArray: Array<LMFilterFieldCriteria>, prefix: string): string | undefined {
    const urlParamArray = filterCriteriaArray.map((filterFieldCriteria: LMFilterFieldCriteria) => {
      const fcPrefix = filterFieldCriteria.exportFilterCriteria ? 'efc' : filterFieldCriteria.isBusClassContextField ? 'bcf' : prefix;
      return this.filterFieldCriteriaToUrlParam(filterFieldCriteria, fcPrefix);
    });

    if (!urlParamArray || urlParamArray.length === 0) {
      return undefined;
    }

    return urlParamArray.join('&');

    // let urlParamString: string = undefined;
    // Observable
    // .from(filterCriteriaArray)
    // .map((filterFieldCriteria: LMFilterFieldCriteria) =>
    //   this.filterFieldCriteriaToUrlParam(filterFieldCriteria, filterFieldCriteria.isBusClassContextField ? 'bcf' : prefix))
    // .toArray()
    // .subscribe((paramList: Array<string>) =>
    //   urlParamString = paramList.join('&'))
    // .unsubscribe();
    //
    // return urlParamString;
  }

  /**
   * Returns LMFilterFieldCriteria as an array of NameValueMaps
   * @param filterCriteriaArray
   * @param                  prefix
   */
  public filterFieldCriteriaToNameValueMap(filterCriteriaArray: LMFilterFieldCriteria[], prefix: string): NameValueMap[] {
    return filterCriteriaArray.map((filterFieldCriteria: LMFilterFieldCriteria) => {
      const fcPrefix = filterFieldCriteria.exportFilterCriteria ? 'efc' : filterFieldCriteria.isBusClassContextField ? 'bcf' :  prefix;
      return this.filterFieldCriteriaToUrlParam(filterFieldCriteria, fcPrefix, true) as NameValueMap;
    });
  }

  private filterFieldCriteriaToUrlParam(filterFieldCriteria: LMFilterFieldCriteria, prefix: string, objectMap = false): string | NameValueMap {
    if (Array.isArray(filterFieldCriteria.value)) {
      let filterQueryString = '';

      if (filterFieldCriteria.value.length === 0) {
        if (filterFieldCriteria?.operator === 'isEmpty' ||
          filterFieldCriteria?.operator === 'isNotEmpty') {
          filterQueryString = `${filterFieldCriteria.operator}()`;
        } else {
          return '';
        }
      }

      for (let i = 0; filterFieldCriteria.value.length > i; i++) {
        if (filterFieldCriteria.operator !== undefined) {
          filterQueryString += filterFieldCriteria.operator;
        }
        filterQueryString += (filterFieldCriteria.value[i] === '') ? '(blank)' : '(' + filterFieldCriteria.value[i] + ')';
        if (filterFieldCriteria.disableTranslationFiltering) {
          filterQueryString += AppConstants.DTF_SUFFIX;
        }
        if (i + 1 === filterFieldCriteria.value.length) {
          break;
        }
        filterQueryString += filterFieldCriteria.operator.startsWith('doesNot') ? '&&' : '||';
      }

      return this.getFilterCriteriaMap(`${prefix}.${filterFieldCriteria.fieldId}`, filterQueryString, objectMap);
    }

    if (filterFieldCriteria?.operator === 'inRange') {
      // todo do we need disableTranslationFiltering implement for a range operator?!? - Phillip 6/5/2024
      const name = `${prefix}.${filterFieldCriteria.fieldId}`;
      const value = `${filterFieldCriteria.operator}(${filterFieldCriteria.value.replace(' - ', ',')})`;
      return this.getFilterCriteriaMap(name, value, objectMap);
    }

    if (filterFieldCriteria.operator !== undefined) {
      const name = `${prefix}.${filterFieldCriteria.fieldId}`;
      const value = `${filterFieldCriteria.operator}(${filterFieldCriteria.value})${filterFieldCriteria.disableTranslationFiltering ? AppConstants.DTF_SUFFIX : ''}`;
      return this.getFilterCriteriaMap(name, value, objectMap);
    }

    // todo when do we end up here? do we need disableTranslationFiltering implemented?!? - Phillip 6/5/2024
    const value = filterFieldCriteria.value + (filterFieldCriteria.disableTranslationFiltering ? AppConstants.DTF_SUFFIX : '');
    return this.getFilterCriteriaMap(`${prefix}.${filterFieldCriteria.fieldId}`, value, objectMap);
  }

  private getFilterCriteriaMap(name: string, value: string, objectMap = false): string | NameValueMap {
    // need to double escape a double-quote
    value = value.replace(/\"/g, '\\\"');

    // When objectMap is true, the value is NOT URL encoded since Angular encodes the HTTPParams in HTTP Get.
    if (objectMap) {
      const map = {};
      map[name] = value;
      return map;
    }

    // This value is added to the URL String and not passed as HTTPParams in HTTP Get. So it has to be encoded.
    return `${encodeURIComponent(name)}=${encodeURIComponent(value)}`;
  }

  public get getDataArea(): string {
    return this.lmContextService.getContext().dataArea;
  }

  public get getWebApp(): string {
    return this.lmContextService.getContext().webApp;
  }

  setAccessibleWebAppsUrlTemplate(url: string) {
    this.accessibleWebAppsUrlTemplate = url;
  }

  setBannerSpecUrlTemplate(url: string) {
    this.bannerSpecUrlTemplate = url;
  }

  public getAccessibleWebAppsUrl() {
    // if (this.lmContextService.allowAnonymousAccess()) {
    return this.resolveUrlTemplateAgainstContext(accessibleWebAppsUrl, this.getWebApp);
    // }
    // return this.resolveUrlTemplateAgainstContext(accessibleWebAppsUrl, 'web');
  }

  public getAccessDeniedUrl() {
    let url = this.getAccessibleWebAppsUrl();
    url += url.indexOf('?') > -1 ? '&' : '?';
    url += 'signout=true';

    return this.resolveUrlTemplateAgainstContext(url, this.getWebApp);
  }

  public getBannerSpecUrl(requestType?: string) {
    let bannerUrl = this.resolveUrlTemplateAgainstContext(bannerSpecUrl);
    bannerUrl = this.removeParams(bannerUrl, [ AppConstants.PARAM_CSK_ASOFDATE, AppConstants.PARAM_CSK_SHOWDELETEDRECORDS, AppConstants.PARAM_CSK_VIEWARCHIVE ]);
    bannerUrl = this.updateUrlParams(bannerUrl, this.addDataContextUrlParams({ }));

    if (requestType) {
      bannerUrl += (bannerUrl.indexOf('?') > -1 ? '&' : '?') + 'requestType=logToServerConsole';
    }

    if (this.lmContextService.isApplicationMenuV2()) {
      bannerUrl +=  (bannerUrl.indexOf('?') > -1 ? '&' : '?') + 'mv=2';
    }

    if (this.lmContextService.isApplicationMenuV3()) {
      bannerUrl +=  (bannerUrl.indexOf('?') > -1 ? '&' : '?') + 'mv=3';
    }

    return bannerUrl;
  }

  public getCaptchaSpecUrl() {
    return `${this.getBaseUrl()}/banner/home?output=spec&requestType=captcha`;
  }

  public getReportUrl(businessClass: string) {
    return this.propagateSessionKeys(`${this.getBaseUrl()}/report/${businessClass}`, '?');
  }

  public get signOutUrl() {
    return this.signOutUrlTemplate;
  }

  public set signOutUrl(url: string) {
    this.signOutUrlTemplate = url;
  }

  private resolveUrlTemplateAgainstContext(url: string, webApp?: string) {
    const context = this.lmContextService.getContext();
    const urlCompiledTemplate = urltemplate.parse(url);
    return urlCompiledTemplate.expand({ urlPdlOrDa: context.urlPdlOrDa, webApp: webApp || context.webApp });
  }

  buildSelectDataViewPagingRequest(selectRequestData: SelectRequestData): SelectPagingRequest {
    if (!selectRequestData) {
      return undefined;
    }

    const requestData: SelectPagingRequest = {
      requestType: 'selectPaging',
      viewType: selectRequestData.viewType,
      viewName: selectRequestData.viewName,
      dataView: selectRequestData.dataView
    };

    if (selectRequestData.userFolder) {
      requestData.userFolder =  selectRequestData.userFolder;
    }
    if (selectRequestData.displayTopNodeAndChildren !== undefined) {
      requestData.displayTopNodeAndChildren = selectRequestData.displayTopNodeAndChildren;
    }
    if (selectRequestData.useTopNodeFilter !== undefined) {
      requestData.useTopNodeFilter = selectRequestData.useTopNodeFilter;
    }
    if (selectRequestData.structureTopNodeFilter !== undefined) {
      requestData.structureTopNodeFilter = selectRequestData.structureTopNodeFilter;
    }
    if (selectRequestData.disableCreationMode !== undefined) {
      requestData.disableCreationMode = selectRequestData.disableCreationMode;
    }
    if (selectRequestData.actionName) {
      requestData.actionName = selectRequestData.actionName;
    }
    if (selectRequestData.pfActionName) {
      requestData.pfActionName = selectRequestData.pfActionName;
    }
    if (selectRequestData.navigationContext) {
      requestData.navigationContext = selectRequestData.navigationContext;
    }

    return requestData;
  }


  buildFilterPreviewRequest(query: string): FilterPreviewRequest {
    return query ?
      {
        requestType: 'lplFilterPreview',
        lplFilter: query
      } : undefined;
  }

  /**
   * Remove the origin from the url to make it a full relative path.
   * For Example:
   * http://localhost:8001/hcm/EmployeeSelfService/home?csk.ng=true
   * - will convert to -
   * /hcm/EmployeeSelfService/home?csk.ng=true
   *
   * @param redirectUrl
   * @returns
   */
  toRelativeUrl(url: string, encodeParens = false, encodePlus = false): string {
    let relativeUrl: string;
    const origin = window.location.origin ? window.location.origin : '';

    if (url.toLowerCase().startsWith(origin.toLowerCase())) {
      relativeUrl = url.substring(origin.length);
      if (relativeUrl.startsWith(':')) {
        // Default port is in the url, but not in window.location.
        // Action redirect navigation urls come with default port, strip the default port.
        relativeUrl = relativeUrl.substring(relativeUrl.indexOf('/'));
      }
    } else {
      relativeUrl = url;
    }

    if (encodePlus) {
      relativeUrl = this.encodePlus(relativeUrl);
    }

    if (encodeParens) {
      relativeUrl = this.encodeParens(relativeUrl);
    }

    return relativeUrl;
  }

  public toFullUrl(url: string): string {
    const protocol = url.substring(0, 4);
    if (protocol === 'http') {
      return url;
    }

    // ----------------------------------------------------------
    // is the url a relative url?
    // ----------------------------------------------------------
    const urlPdlOrDa = this.lmContextService.getUrlPdlOrDa();
    if (url.startsWith('/' + urlPdlOrDa)){
      return window.location.protocol + '//' + window.location.host + url;
    }
    return window.location.protocol + '//' + window.location.host + '/' + urlPdlOrDa + url;
  }

  /**
   * Open a file url with a unique number so a new tab opens each time
   * regardless of whether it's the same url or not.
   *
   * @param url location of the file
   */
  public openFile(url: string): void {
    const pdfWindow = this.openExternalUrl(url);
  }

  /**
   * See http://stackoverflow.com/questions/7501424/set-title-in-the-window-popup for more details
   *
   * @param pdfWindow
   * @param url
   * @param title
   */
  private checkPdf(pdfWindow, url, title): void {
    if (pdfWindow.document) {
      pdfWindow.document.write(
        '<html>' +
        '<head><title>' + title + '</title></head>' +
        '<body height="100%" width="100%"><embed src="' + url + '" type="application/pdf" height="100%" width="100%" /></body>' +
        '</html>'
      );
    } else {
      // if not loaded yet
      setTimeout(() => {
        this.checkPdf(pdfWindow, url, title);
      }, 10); // check in another 10ms
    }
  }

  // todo ppatton claudenbach this one doesn't seem to work.
  // private checkPdf(pdfWindow, title): void {
  //   if (pdfWindow.document) {
  //     pdfWindow.document.title = title;
  //   } else {
  //     setTimeout(() => { this.checkPdf(pdfWindow, title); }, 10); // check in another 10ms
  //   }
  // }

  public stripDefaultPort(url: string) {
    url = url.replace(':80/', '/');
    return url.replace(':443/', '/');
  }

  // Only to be used in IE
  public stripDefaultPortFromHost(url: string) {
    url = url.replace(':80', '');
    return url.replace(':443', '');
  }

  // ----------------------------------------------------------------------
  // todo this should be moved to another service so that we don't need
  //  to inject SohoMessageService in the depencencies
  // ----------------------------------------------------------------------
  public openExternalUrl(url: string): void {
    url = this.encodePlus(url);
    url = this.encodeParens(url);

    if (this.lmContextService.isMobileDevice()) {
      if (this.lmContextService.insideInforGo()) {
        // InforGo requires a full url when navigating to an internal page or external page
        // check for missing host / port and appened if needed.
        const protocol = url.substring(0, 4);
        if (protocol.toLowerCase() !== 'http') {
          url = window.location.protocol + '//' + window.location.host + url;
        }

        // check for same domain
        // if the domain is the same then open url in InforGo webview
        // else open in their mobile_openLinkInExternalBrowser
        const origin = window.location.origin ? window.location.origin : '';

        if (url.toLowerCase().startsWith(origin.toLowerCase())) {
          window.open(url, '_blank');
        } else {
          // need to find a way to get this into lm-context-util.service.ts
          const data = { link: url };
          infor.companyon.client.sendMessage('mobile_openLinkInExternalBrowser', data);
        }
      } else {
        const popup_window = window.open(url);
        try {
          popup_window.focus();
        } catch (e) {
          const buttons = [
            {
              id: null,
              text: Soho.Locale.translate(LMDialogButtonKey.CLOSE),

              // eslint-disable-next-line @typescript-eslint/no-shadow
              click: (e, modal) => {
                modal.close();
              }
            }
          ];
          this.sohoMessageService
            .message()
            .message(Soho.Locale.translate('PopupBlocker'))
            .title('')
            .buttons(buttons)
            .open();
          return;
        }
      }
    } else {
      window.open(url, '_blank');
    }
  }

  private getParams(url: string): any {
    const nameValuePairs = {};
    if (url) {
      url = this.removeHash(url);
      const paramStart: number = url.indexOf('?');
      if (paramStart < 0) {
        return [];
      }
      const paramsStr = url.substring(paramStart + 1);
      const params = paramsStr.split('&');

      params.forEach((param) => {
        const paramPairs = param.split('=');
        nameValuePairs[paramPairs[ 0 ]] = paramPairs[ 1 ];
      });
    }
    return nameValuePairs;
  }

  public remove3XParams(url: string): string  {
    url = this.removeParam(url, 'csk.3x');
    url = this.removeParam(url, 'csk.hidemenubar');
    url = this.removeParam(url, 'csk.hidetitlebar');
    return url;
  }

  public remove3XParam(url: string): string  {
    url = this.removeParam(url, 'csk.3x');
    return url;
  }

  /**
   * Removes csk params from the url that are not in the LMGlobal Context object.
   */
  public removeCsksNotInGlobalContext(url: string): string {
    const context = this.lmContextService.getContext();

    const keys = [
      'csk.ng',
      'csk.cli',
      'csk.showusingxi',
      'csk.ng3x',
      'csk.displayType',
    ];

    keys.filter(key =>
      // filter for csk params that aren't in LMContext
      !context.contextSessionKeys || !context.contextSessionKeys[ key ])
      .forEach(key =>
      // remove those params from the url
        url = this.removeParam(url, key));

    return url;

    // const cskParamsNotInContext = this.getCskParamsNotInContext(url);
    //
    // Object.keys(cskParamsNotInContext)
    // .forEach(key => cskParamsNotInContext[key] = null);
    //
    // const newUrl = this.updateUrlParams(url, cskParamsNotInContext);
    // return newUrl;
  }

  /**
   * Look for each csk in the url that is not in the global context and return it.
   * Params in the paramsToIgnore param are not included in the returned object.
   */
  public getCskParamsNotInContext(url: string, paramsToIgnore: string[]): Params {
    const context = this.lmContextService.getContext();
    const cskParams = this.getPrefixedParams(url, 'csk.');
    const cskParamsNotInContext: Params = {};

    Object.keys(cskParams)
    // if key is not in contextSessionKeys
      .filter(key => !context.contextSessionKeys || !context.contextSessionKeys[ key ])
    // if key is not in ignore list
      .filter(key => !paramsToIgnore || !paramsToIgnore.includes(key))
    // then add it to list of csk params not in context
      .forEach(key => cskParamsNotInContext[key] = cskParams[key]);

    return cskParamsNotInContext;
  }

  public stripCSKs(paramArray: Array<string>): Array<string> {
    return paramArray.filter(k => !k.startsWith('csk.'));
  }

  public hasParam(url: string, param: string): boolean {
    return this.getUrlParam(url, param) !== null;
  }

  /**
   * removes mulitple params from the url.
   */
  public removeParams(url: string, params: string[]): string {
    params.forEach(param => url = this.removeParam(url, param));
    return url;
  }

  /**
   * removes a param from the url.
   */
  public removeParam(url: string, paramIn: string): string {
    const paramStart: number = url.indexOf('?');
    if (paramStart < 0) {
      return url;
    }

    // perhaps overkill but being super safe here to avoid javascript errors
    let hash: string | undefined;
    if (url && url.indexOf('#') > -1) {
      const parts = url.split('#');
      url = parts[0];
      hash = parts[1];
    }

    const paramsStr = url.substring(paramStart + 1);
    const params = paramsStr.split('&');
    const goodParams = new Array<string>();

    params.filter((param) => {
      const paramPairs = param.split('=');
      if (paramPairs[ 0 ] !== paramIn) {
        goodParams.push(param);
      }
    });

    return url.substring(0, paramStart) + (goodParams.length > 0 ? '?' + goodParams.join('&') : '') + (hash ? '#' + hash : '');

    // todo ppatton Refactor this function to be simpler; like below
    //  Building a URL, with buildURLFromHref, encodes it's internal href.
    //  Will that be an issue for the result we return?
    // const urlObject = this.buildURLFromHref(url);
    // if (!urlObject.searchParams.has(paramIn)) {
    //   return url;
    // }
    //
    // urlObject.searchParams.delete(paramIn);
    // return urlObject.href;
  }

  public removeFilterCriteria(url: string): string {
    const paramStart: number = url.indexOf('?');

    if (paramStart < 0) {
      return url;
    }

    const paramsStr = url.substring(paramStart + 1);
    const params = paramsStr.split('&');
    const goodParams = new Array<string>();

    params.filter((param) => {
      const paramPairs = param.split('=');
      if (!paramPairs[0].startsWith('fc.') && !paramPairs[0].startsWith('bcf.')) {
        goodParams.push(param);
      }
    });

    return url.substring(0, paramStart) + (goodParams.length > 0 ? '?' + goodParams.join('&') : '');
  }

  /**
   * @deprecated no longer seems to be used
   */
  public replaceUrlParams(url: string, newUrlParams: LMUrlParams, addCsks = true): string {
    let newUrl = url.substr(0, url.indexOf('?'));
    let delimiter = '?';
    if (Object.keys(newUrlParams).length > 0) {
      newUrl = newUrl + delimiter + this.objectToQueryString(newUrlParams);
      delimiter = '&';
    }
    if (addCsks) {
      newUrl = this.propagateSessionKeys(newUrl, delimiter);
    }
    return newUrl;
  }

  public getWebAppNameFromUrl(url: string): string {
    const pathname = this.getUrlPath(url);
    const isGenWebApp = this.isGenUrlUsingHref(url);
    return this.getUrlPathValue(pathname, AppConstants.URLSEG_WEBAPP_INDEX, isGenWebApp);
  }

  public getHeaderForUserTimeZone(): any {
    try {
      const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      if (userTimeZone) {
        return { headers: { 'user.timezone.id': userTimeZone } };
      }

      const rightNow = new Date();
      const stdTime = new Date(rightNow.getFullYear(), 0, 1, 0, 0, 0, 0);
      const dstTime = new Date(rightNow.getFullYear(), 6, 1, 0, 0, 0, 0);

      const offset: string = (-(stdTime.getTimezoneOffset() / 60)).toString();
      const dstOffset: string = (-(dstTime.getTimezoneOffset() / 60)).toString();
      return { headers:
          { 'user.timezone.offset': offset,
            'user.timezone.dst':    dstOffset } };
    } catch (e) {
      // fail safe
      return {};
    }
  }

  /**
   * Return the bannerSpec for the given url.
   * @returns
   */
  public getUserContextUrl(): string {
    let bannerUrl = this.getBannerSpecUrl();
    const delimiter = bannerUrl.indexOf('?') > -1 ? '&' : '?';
    bannerUrl += delimiter + 'requestType=userContext';

    return bannerUrl;
  }

  public getPrefixedParams(url: string, prefix: string): Params {
    const urlParams = this.getUrlParams(url);
    return this.getPrefixedParamsFromParams(urlParams, prefix);

    // const urlParamsWithPrefix = {} as Params;
    //
    // Object.keys(urlParams)
    //   .filter(key => key.startsWith(prefix))
    //   .map(key => urlParamsWithPrefix[key] = urlParams[key]);
    //
    // return urlParamsWithPrefix;
  }

  public getPrefixedParamsFromParams(urlParams: Params, prefix: string, decode?: boolean): Params {
    const urlParamsWithPrefix = {} as Params;
    Object.keys(urlParams)
      .filter(key => key.startsWith(prefix))
      .map(key => urlParamsWithPrefix[key] = decode ? decodeURIComponent(urlParams[key]) : urlParams[key]);

    return urlParamsWithPrefix;
  }

  /**
   * @deprecated use #getPrefixedParams instead. it's definitely faster.
   */
  private getPrefixedParamsFromSearchParams(searchParams: URLSearchParams, prefix: string): Params {
    const params: Params = {};

    searchParams.forEach((value, key) => {
      if (key.startsWith(prefix)) {
        params[key] = value;
      }
    });

    return params;
  }

  // todo ppatton no usages found - remove me at some point - 9/17/2020
  // public paramsToFilterCriteria(queryParams: Params): LMFilterFieldCriteria[] {
  //   const filterCriteria: LMFilterFieldCriteria[] =
  //     Object.keys(queryParams)
  //     // todo not sure we need flc prefixes in this filter. - Phillip
  //     .filter(key => key.startsWith('fc.') || key.startsWith('flc.') || key.startsWith('bcf.'))
  //     .map<LMFilterFieldCriteria>(key => this.buildFilterCriteria(key, queryParams[key]));
  //
  //   return filterCriteria;
  // }

  public buildFilterCriteria(key: string, val: string): LMFilterFieldCriteria {
    /**
     * [^.]+$
     *   Match a single character not present in the string [^.]+
     *     + Quantifier - Matches between one and unlimited times, as many times as possible, giving back as needed (greedy)
     *     . matches the character . literally (case sensitive)
     */
    const fieldId = key.match('[^.]+$')[0];

    /**
     * ^[^\(]+
     *   ^ asserts position at start of the string
     *     Match a single character not present in the string [^\(]+
     *       + Quantifier - Matches between one and unlimited times, as many times as possible, giving back as needed (greedy)
     *       \( matches the character ( literally (case sensitive)
     */
    const operator = val.match('^[^\(]+')[0] as LMFilterFieldOperator;

    /**
     * \(([^)]+)\)
     *   \( matches the character ( literally (case sensitive)
     *     1st Capturing Group ([^)]+)
     *       Match a single character not present in the list below [^)]+
     *         + Quantifier - Matches between one and unlimited times, as many times as possible, giving back as needed (greedy)
     *         ) matches the character ) literally (case sensitive)
     *   \) matches the character ) literally (case sensitive)
     */
    const value = val.match('\\(([^)]+)\\)')[1];

    return { operator, fieldId, value };
  }

  public isAbsoluteUrl(httpUrl: string): boolean {
    /**
     * (?:^[a-z][a-z0-9+.-]*:|\/\/)
     *   Non-capturing group (?:^[a-z][a-z0-9+.-]*:|\/\/)
     *     1st Alternative ^[a-z][a-z0-9+.-]*:
     *       ^ asserts position at start of a line
     *          Match a single character present in the list below [a-z]
     *            a-z a single character in the range between a (index 97) and z (index 122) (case insensitive)
     *          Match a single character present in the list below [a-z0-9+.-]*
     *            Quantifier — Matches between zero and unlimited times, as many times as possible, giving back as needed (greedy)
     *              a-z a single character in the range between a (index 97) and z (index 122) (case insensitive)
     *              0-9 a single character in the range between 0 (index 48) and 9 (index 57) (case insensitive)
     *              +.- matches a single character in the list +.- (case insensitive)
     *          : matches the character : literally (case insensitive)
     *     2nd Alternative \/\/
     *       \/ matches the character / literally (case insensitive)
     *       \/ matches the character / literally (case insensitive)
     *
     * https://stackoverflow.com/questions/10687099/how-to-test-if-a-url-string-is-absolute-or-relative
     * Test with https://regex101.com/r/nR2yL6/3
     * todo Proper solution fails. I'm not sure what I'm doing wrong with this regex.
     *   return !!httpUrl.match('?:^[a-z][a-z0-9+.-]*:|\/\/)/gm');
     */
    //  alternative considered to be lacking.
    return (httpUrl.indexOf('://') > 0 || httpUrl.indexOf('//') === 0);
  }

  /**
   * Return the bannerSpec for the given url.
   * @returns
   */
  public getTelegrafUrl(): string {
    let telegrafUrl = this.getBannerSpecUrl();
    const delimiter = telegrafUrl.indexOf('?') > -1 ? '&' : '?';
    telegrafUrl += delimiter + 'requestType=telegraf';

    return telegrafUrl;
  }

  public getInteruptThreadUrl(): string {
    let interuptThreadUrl = this.getBannerSpecUrl();
    const delimiter = interuptThreadUrl.indexOf('?') > -1 ? '&' : '?';
    interuptThreadUrl += delimiter + 'requestType=interruptThreads';

    return interuptThreadUrl;
  }

  // -------------------------------------------------------------------------
  // todo ppatton these build function should really be moved into their own
  //  service and not be considered part of the standard url utitilities.
  // -------------------------------------------------------------------------
  public buildListInitialDataUrl(urlInfo: LMListUrlInfo, listSpec: LMListSpec, options?: LMListUrlInfoOptions): string {
    let urlParams: LMUrlParams = { pageop: 'load', pagesize: listSpec.pageSize };
    const urlParamsToBeEncoded: LMUrlParams = {};

    if (listSpec.dependentList) {
      // if building a related url sourceResourceType/id must exist
      urlParams.dependentList = true;
      const keyedResource = urlInfo.sourceResource as KeyedResource;
      urlParams[urlInfo.sourceResource.resourceType] = '{resourceId}.' + keyedResource.uiResourceName;

    } else if (urlInfo.sourceResource) {
      urlParams[urlInfo.sourceResource.resourceType] = (urlInfo.sourceResource as Resource).generateResourceId(true);

      if (urlInfo.httpUrl && urlInfo.sourceResource.resourceType === 'select') {
        const selectLinkParam = urlInfo.urlParams['selectlink'];
        if (selectLinkParam) {
          urlParamsToBeEncoded.selectlink = selectLinkParam;
        }
      }
    }

    if (listSpec.pageSize !== -1 && listSpec.busListSpec?.defaultSortOrder) {
      // this needs to be encoded for columnar sortOrderNames because they may have a ':' colon in them.
      urlParamsToBeEncoded.sortOrderName = listSpec.busListSpec.defaultSortOrder.sortOrderName;
      urlParams.isAscending = listSpec.busListSpec.defaultSortOrder.isAscending;
    }

    if (urlInfo.httpUrl) {
      const filter: string | null = urlInfo.urlParams['filter'];
      if (filter && filter.length > 0) {
        urlParams.filter = filter;
      }

      // const lplFilter: string = urlInfo.urlParams['filter'];
      // if (lplFilter) {
      //   const dLplFilter = decodeURIComponent(lplFilter);
      //   urlParamsToBeEncoded.filter = dLplFilter;
      // }

      // const filterCriteriaParams: Params = this.getPrefixedParams(urlInfo.url.href, 'fc');
      const filterCriteriaParams: Params = this.getPrefixedParamsFromParams(urlInfo.urlParams, 'fc');
      urlParams = { ...urlParams, ...filterCriteriaParams };

      // const filterListCriteriaParams: Params = this.getPrefixedParams(urlInfo.url.href, 'flc');
      const filterListCriteriaParams: Params = this.getPrefixedParamsFromParams(urlInfo.urlParams, 'flc');
      urlParams = { ...urlParams, ...filterListCriteriaParams };

      const userFolder: string = listSpec.userFolder; // urlInfo.url.searchParams.get('userfolder');
      if (userFolder) {
        urlParamsToBeEncoded.userfolder = userFolder;
      }

      // Drills from aggregation charts
      if (urlInfo.urlParams['aggDrill']) {
        urlParams.aggDrill = true;
        urlParams.savedFilter = urlInfo.urlParams['savedFilter'];
      }
      // ------------------------------------------------------------------
      // in case the url is a list url with a pagepanel url param.
      // for example when control shift clicking from a page panel list.
      // ------------------------------------------------------------------
      // if (!options || !options.pagePaneSpec && !listSpec.busListSpec.implementsCubeView) {
      //   const pagePanel: string = urlInfo.url.searchParams.get('pagepanel');
      //   if (urlParams.pagepanel && pagePanel) {
      //     urlParams.pagepanel = pagePanel;
      //   }
      // }

      // ------------------------------------------------------------------
      // in case the url is a list url with fld url params.
      // for example when control shift clicking from a filter list.
      // ------------------------------------------------------------------
      if (!options?.additionalFieldIds) {
        const additionalFields: any = urlInfo.urlParams['fld']; // urlInfo.url.searchParams.getAll('fld');
        if (additionalFields) {
          urlParams.fld = additionalFields;
        }
      }
    }

    /**
     * Needed for filter lists. See usage in lm-detail-section-list.component.ts
     */
    if (options?.additionalFieldIds?.length > 0) {
      urlParams.fld = options.additionalFieldIds;
    }

    if (urlInfo.fromListResource) {
      urlParams.fromlist = (urlInfo.fromListResource as KeyedResource).generateResourceId(true);
    }

    // todo : SpecUrlRefactorError_08262020 - building csk from global context only but mingle drillback
    //  urls (done from mingle widgets) sometime add other csks using them as an API (like csk.IsoLocale=fr).
    urlParams = { ...urlParams, ...this.lmContextService.getContext().contextSessionKeys };
    if (urlInfo.contextSessionKeys) {
      urlParams = { ...urlParams, ...urlInfo.contextSessionKeys };
    }

    if (options?.pagePaneSpec && !listSpec.busListSpec.implementsCubeView) {
      if (options.pagePaneSpec.pane && options.pagePaneSpec.pane.pagePanelResourceId) {
        urlParams.pagepanel = options.pagePaneSpec.pane.pagePanelResourceId;
      } else if (options.pagePaneSpec.pagePanelResourceId) {
        urlParams.pagepanel = options.pagePaneSpec.pagePanelResourceId;
      }
    }

    if (listSpec.isTemplateList) {
      urlParams.isTemplateList = true;
    }

    return LMUrlUtilService.buildUrl2(
      urlInfo.basePath,
      urlInfo.resource.resourceType,
      urlInfo.resource.generateResourceId(true),
      urlParamsToBeEncoded,
      urlParams);
  }

  public buildPageInitialDataUrl(urlInfo: LMPageUrlInfo, formSpec: LMPageSpec): string {
    let urlParams: LMUrlParams = { pageop: 'load', pagesize: 1 };

    const formResourceType = urlInfo.resource.resourceType;
    const formResourceId = urlInfo.resource.resourceId;

    if (urlInfo.sourceResource) {
      urlParams[urlInfo.sourceResource.resourceType] = urlInfo.sourceResource.generateResourceId(true);
    }

    if (urlInfo.navigationContext) {
      urlParams = { ...urlParams, ...urlInfo.navigationContext };
    }

    urlParams = { ...urlParams, ...this.lmContextService.getContext().contextSessionKeys };
    if (urlInfo.contextSessionKeys) {
      urlParams = { ...urlParams, ...urlInfo.contextSessionKeys };
    }

    return LMUrlUtilService.buildUrl2(urlInfo.basePath, formResourceType, formResourceId, null, urlParams);
  }

  public buildFormInitialDataUrl(urlInfo: LMFormUrlInfo, formSpec: LMFormSpec): string {
    if (formSpec.busFormSpec.formType === 'Matrix') {
      return this._buildMatrixFormLoadDataUrl(urlInfo, formSpec);
    }

    if (formSpec.templateTargetResourceId) {
      return this._buildTemplateFormLoadDataUrl(urlInfo);
    }

    return this._buildFormInitialDataUrl(urlInfo, formSpec);
  }

  /**
   * todo: spec-url-refactor this is just a subset of _buildFormInitialDataUrl, why not combine them?!
   */
  private _buildMatrixFormLoadDataUrl(urlInfo: LMFormUrlInfo, formSpec: LMFormSpec): string {
    let urlParams: LMUrlParams = { pageop: 'load', pagesize: 1 };
    const urlParamsToBeEncoded: LMUrlParams = {};

    const formResourceType = urlInfo.resource.resourceType;
    const resourceName = urlInfo.resource.uiResourceName;
    const formResourceId = '{resourceId}' + (resourceName ? '.' + resourceName : '');

    if (urlInfo.sourceResource) {
      urlParams[urlInfo.sourceResource.resourceType] =  urlInfo.sourceResource.generateResourceId(true);

      // Do not add fromlist to dependent form. From list is used for pagination in the main form and not in dependent form
      if (!formSpec.dependentForm && !formSpec.independentForm && urlInfo.fromListResource) {
        if (urlInfo.sourceResource.resourceType === 'navigation'
        ||  urlInfo.sourceResource.resourceType === 'relation'
        ||  urlInfo.sourceResource.resourceType === 'drill') {
          urlParams['fromlist'] = urlInfo.fromListResource.generateResourceId(true);
        }
      }
    }

    if (urlInfo.urlParams) {
      let matrixColumns = urlInfo.urlParams['item'];
      if (matrixColumns) {
        if (!(matrixColumns instanceof Array)) {
          matrixColumns = [ matrixColumns ];
        }
        // Need to encode using KeyedResource.generateResourceId. This ensures
        // the resourceId is being encoded correctly.
        urlParams.item = this.encodeResourceIds(matrixColumns);
      }
    }

    urlParams = { ...urlParams, ...this.lmContextService.getContext().contextSessionKeys };
    if (urlInfo.contextSessionKeys) {
      urlParams = { ...urlParams, ...urlInfo.contextSessionKeys };
    }

    return LMUrlUtilService.buildUrl2(urlInfo.basePath, formResourceType, formResourceId, urlParamsToBeEncoded, urlParams);
  }

  /**
   * Takes a resourceId array and ensures they have been encoded correctly.
   * @param resourceIds an array of resourceIds
   */
  private encodeResourceIds(resourceIds: string[]): string[]  {
    return resourceIds.map(resourceId =>
      new KeyedResource({ resourceId }).generateResourceId(true));
  }

  private _buildTemplateFormLoadDataUrl(urlInfo: LMFormUrlInfo): string {
    let urlParams: LMUrlParams = { pageop: 'TEMPLATE' };

    const formResourceType = urlInfo.resource.resourceType;
    const resourceName = urlInfo.resource.uiResourceName;
    const formResourceId = '{resourceId}' + (resourceName ? '.' + resourceName : '');

    if (urlInfo.sourceResource) {
      urlParams[ urlInfo.sourceResource.resourceType ] = '{sourceResourceId}';
    }

    urlParams = { ...urlParams, ...this.lmContextService.getContext().contextSessionKeys };
    if (urlInfo.contextSessionKeys) {
      urlParams = { ...urlParams, ...urlInfo.contextSessionKeys };
    }

    return LMUrlUtilService.buildUrl2(urlInfo.basePath, formResourceType, formResourceId, null, urlParams);
  }

  private _buildFormInitialDataUrl(urlInfo: LMFormUrlInfo, formSpec: LMFormSpec): string {
    let urlParams: LMUrlParams = { pageop: 'load', pagesize: 1 };
    let urlParamsToBeEncoded: LMUrlParams = {};

    const formResourceType = urlInfo.resource.resourceType;
    const resourceName = urlInfo.resource.uiResourceName;
    let formResourceId;

    if (formSpec.busFormSpec.implementsCartView) {
      formResourceId = formSpec.resourceId;
    } else {
      formResourceId = '{resourceId}' + (resourceName ? '.' + resourceName : '');
    }

    // 1. Add Action template for form urls
    if (urlInfo.action) {
      if (urlInfo.action === '{actionName}') {
        // if action is just a template string then propagate action template string
        urlParams.action = urlInfo.action;
      } else {
        // if action is not a template string then it's an action name. Add a
        // template string for it only if it's an action in the busClassSpec.actions.
        const actionSpec = this.getActionByName(formSpec.busClassSpec.actions, urlInfo.action);
        if (actionSpec) {
          urlParams.action = '{actionName}';
        }
      }
    }

    // 2. Add the source resource Id
    if (formSpec.busFormSpec.implementsCartView) {
      urlParams['relation'] = formSpec.busFormSpec.cartRelation;
    } else if (urlInfo.sourceResource) {
      urlParams[urlInfo.sourceResource.resourceType] = urlInfo.sourceResource.generateResourceId(true);

      // Do not add fromlist to dependent form. From list is used for pagination in the main form and not in dependent form
      if (!formSpec.dependentForm && !formSpec.independentForm && urlInfo.fromListResource) {
        if (urlInfo.sourceResource.resourceType === 'navigation'
        ||  urlInfo.sourceResource.resourceType === 'relation'
        ||  urlInfo.sourceResource.resourceType === 'drill') {
          urlParams['fromlist'] = urlInfo.fromListResource.generateResourceId(true);
        }
      }
    }

    // Remove source resource of type navigation for Independent form since the main
    // composite form already has all the required keys.
    if (formSpec.independentForm && urlInfo.sourceResource && urlInfo.sourceResource.resourceType === 'navigation') {
      delete urlParams.navigation;
    }

    if (formSpec.dependentForm) {
      // Eg. List detail section through a relation. From Resource Id (list row selection)
      // will be used to build the relation resource Id.
      urlParams.dependentForm = true;

      // if building a related url sourceResourceType/id must exist.
      // place in nonEncodedParamMap so buildUrl doesn't encode the curly braces.
      if (urlInfo.sourceResource) {
        let fromResourceIdTemplate;

        if (urlInfo.sourceResource.resourceType === 'biDrill') {
          fromResourceIdTemplate = '{sourceResourceId}';
        } else {
          const keyedResource = urlInfo.sourceResource as KeyedResource;
          fromResourceIdTemplate = '{sourceResourceId}.' + keyedResource.uiResourceName;
        }
        urlParams[urlInfo.sourceResource.resourceType] = fromResourceIdTemplate;

        // todo: spec-url-refactor no need as the setting this above will override the current property value.
        // // Remove the source resource type parameter added above since we need a templated
        // // resource id in this case. The list record selection will provide the from resource id.
        // delete urlParams[urlInfo.sourceResource.resourceType];
      }
    }

    if (urlInfo.urlParams) {
      // 3. Add the pagination parameters. DataViewSet is not available, so replicate what is available in the request
      // todo use urlInfo.urlParams instead of this.hasParam(urlInfo.httpUrl, param))
      AppConstants.LM_FORM_URL_PAGING_PARAMS
        .filter(param => this.hasParam(urlInfo.httpUrl, param))
        .forEach(param => {
          const paramValue = this.getUrlParam(urlInfo.httpUrl, param);
          if (param === 'lk' || param === 'fk' || param === 'pk') {
            const resource = new KeyedResource({ resourceId: paramValue });
            urlParams[param] = resource.generateResourceId(true);
          } else {
            urlParams[param] = paramValue;
          }
        });

      // 3.1 Add more pagination parameters related to dependent keys from request
      // urlInfo.url.searchParams.forEach((value, key) => {
      //   if (key.startsWith('lk.') || key.startsWith('fk.') || key.startsWith('pk.')) {
      //     urlParamsToBeEncoded[ key ] = value;
      //   }
      // });
      const fkDepKeys: Params = this.getPrefixedParamsFromParams(urlInfo.urlParams, 'fk.');
      Object.keys(fkDepKeys).forEach((key: string) => {
        urlParams[ key ] = this.generateEncodedResourceId(fkDepKeys[ key ]);
      });

      const lkDepKeys: Params = this.getPrefixedParamsFromParams(urlInfo.urlParams, 'lk.');
      Object.keys(lkDepKeys).forEach((key: string) => {
        urlParams[ key ] = this.generateEncodedResourceId(lkDepKeys[ key ]);
      });

      const pkDepKeys: Params = this.getPrefixedParamsFromParams(urlInfo.urlParams, 'pk.');
      Object.keys(pkDepKeys).forEach((key: string) => {
        urlParams[ key ] = this.generateEncodedResourceId(pkDepKeys[ key ]);
      });

      // 4. Add Filter criteria
      const filter: string | null = urlInfo.urlParams['filter'];
      if (filter && filter.length > 0) {
        urlParams.filter = filter;
      }

      const filterCriteriaParams: Params = this.getPrefixedParamsFromParams(urlInfo.urlParams, 'fc');
      urlParams = { ...urlParams, ...filterCriteriaParams };

      const filterListCriteriaParams: Params = this.getPrefixedParamsFromParams(urlInfo.urlParams, 'flc');
      urlParams = { ...urlParams, ...filterListCriteriaParams };

      // Is this needed for form initial load url?
      const additionalFields: any = urlInfo.urlParams['fld']; // urlInfo.url.searchParams.getAll('fld');
      if (additionalFields) {
        urlParams.fld = additionalFields;
      }
      // const additionalFields: string[] = urlInfo.url.searchParams.getAll('fld');
      // if (additionalFields?.length > 0) {
      //   urlParams.fld = additionalFields;
      // }

      // 5. Matrix Form
      // todo not sure why this is here, we use buildMatrixLoadDataUrl() to
      //  build matrix loadDataUrl instead of this function. - Phillip 5/16/2024
      const matrixColumns = urlInfo.urlParams['item'];
      if (matrixColumns) {
        urlParamsToBeEncoded.item = matrixColumns;
      }
      // const matrixColums: string[] = urlInfo.url.searchParams.getAll('item');
      // if (matrixColums && matrixColums.length > 0) {
      //   urlParamsToBeEncoded.item = matrixColums;
      // }
    }

    // 6. Add ncs
    if (urlInfo.navigationContext) {
      urlParamsToBeEncoded = { ...urlParamsToBeEncoded, ...urlInfo.navigationContext };
    }

    // todo : SpecUrlRefactorError_08262020 - building csk from global context only but mingle drillback
    //  urls (done from mingle widgets) sometime add other csks using them as an API (like csk.IsoLocale=fr).
    urlParams = { ...urlParams, ...this.lmContextService.getContext().contextSessionKeys };
    if (urlInfo.contextSessionKeys) {
      urlParams = { ...urlParams, ...urlInfo.contextSessionKeys };
    }

    /**
     * fromTemplateList url param is required when opening a template target dataView from a template list,
     * but only when the template target record does not exist. This param causes the server to
     * go through a bit of a different path to get the correct dataView.
     *
     * Changed this condition when building the initial data url to include 'fromTemplateList'
     * as a URL param on the client when opening a template target data in create mode.
     * See LMWEB-19003 PO Receipt Adjustment Line Details missing
     * - Phillip 1/25/24
     */
    if (urlInfo.listResource && urlInfo.templateTargetBusClass) {
      urlParams.fromTemplateList = true;
    }

    // build the new data url
    return LMUrlUtilService.buildUrl2(urlInfo.basePath, formResourceType, formResourceId, urlParamsToBeEncoded, urlParams);
  }

  private generateEncodedResourceId(resourceId: string): string {
    const resource: KeyedResource = new KeyedResource({ resourceId });
    return resource.generateResourceId(true);
  }

  public buildMatrixCellTransactionUrl(urlInfo: LMFormUrlInfo, formSpec: LMFormSpec): string {
    if (formSpec.busFormSpec.formType !== 'Matrix') {
      return null;
    }
    const matrixCellResource = new KeyedResource({ resourceType: 'form', resourceId: formSpec.matrixCellResource });
    const urlParams: LMUrlParams = { action: '_execute' };
    if (urlInfo.fromListResource) {
      urlParams['fromlist'] = urlInfo.fromListResource.generateResourceId(true);
    }

    return this.buildTemplateUrl(urlInfo, matrixCellResource, null, urlParams, false);
  }

  public buildMatrixColumnTransactionUrl(urlInfo: LMFormUrlInfo, formSpec: LMFormSpec): string {
    if (formSpec.busFormSpec.formType !== 'Matrix') {
      return null;
    }

    const matrixColumnResource = new KeyedResource({ resourceType: 'form', resourceId: formSpec.matrixColumnResource });
    const urlParams: LMUrlParams = { action: '_execute' };
    if (urlInfo.fromListResource) {
      urlParams['fromlist'] = urlInfo.fromListResource.generateResourceId(true);
    }

    return this.buildTemplateUrl(urlInfo, matrixColumnResource, null, urlParams, false);
  }

  public buildFormInvokeCreateUrl(urlInfo: LMFormUrlInfo): string {
    if (!urlInfo.sourceResource || urlInfo.sourceResource.resourceType !== AppConstants.RESOURCETYPE_FORMINVOKE) {
      return null;
    }

    let urlParams: LMUrlParams = { action: '_execute', ...this.lmContextService.getContext().contextSessionKeys };
    if (urlInfo.contextSessionKeys) {
      urlParams = { ...urlParams, ...urlInfo.contextSessionKeys };
    }

    urlParams[urlInfo.sourceResource.resourceType] = urlInfo.sourceResource.generateResourceId(true);


    // build the new data url
    return LMUrlUtilService.buildUrl2(
      urlInfo.basePath,
      urlInfo.resource.resourceType,
      urlInfo.resource.generateResourceId(true),
      null,
      urlParams);
  }

  public buildSubPanelDataUrl(urlInfo: LMFormUrlInfo, panelName: string): string {
    let urlParams: LMUrlParams = { pageop: 'subpanels' };

    urlParams = { ...urlParams, ...this.lmContextService.getContext().contextSessionKeys };
    if (urlInfo.contextSessionKeys) {
      urlParams = { ...urlParams, ...urlInfo.contextSessionKeys };
    }

    const resourceId = '{resourceId}.' + urlInfo.resource.uiResourceName + '.' + panelName;
    return LMUrlUtilService.buildUrl2(urlInfo.basePath, urlInfo.resource.resourceType, resourceId, null, urlParams);
  }

  public buildListTransactionUrl(urlInfo: LMBaseUrlInfo, listSpec: LMListSpec): string {
    const urlParamsToBeEncoded: LMUrlParams = {};

    const userFolder: string = listSpec.userFolder; // urlInfo.url.searchParams.get('userfolder');
    if (userFolder) {
      urlParamsToBeEncoded.userfolder = userFolder;
    }

    return this.buildTemplateUrl(urlInfo, urlInfo.resource as KeyedResource, null, { action: '_execute' }, false, urlParamsToBeEncoded);
  }

  public buildFormTransactionUrl(urlInfo: LMBaseUrlInfo, formSpec?: LMFormSpec): string {
    if (formSpec.independentFormResourceId) {
      const independentFormResource = new KeyedResource({ resourceType: 'form', resourceId: formSpec.independentFormResourceId });
      return this.buildTemplateUrl(urlInfo, independentFormResource, urlInfo.sourceResource as KeyedResource, { action: '_execute' }, false);
    }

    const urlParams: LMUrlParams = { action: '_execute'};
    if (urlInfo.fromListResource) {
      urlParams['fromlist'] = urlInfo.fromListResource.generateResourceId(true);
    }

    return this.buildTemplateUrl(
      urlInfo,
      urlInfo.resource as KeyedResource,
      urlInfo.sourceResource as KeyedResource,
      urlParams,
      formSpec.dependentForm);
  }

  /**
   * Build a template url for view audit log redirects.
   * @param urlInfo
   * @return A template url to use when building actionRedirectUrls.
   */
  public buildViewAuditLogRedirectUrl(urlInfo: LMBaseUrlInfo): string {
    const resourceType: LMResourceType = 'audit';
    const resourceId = '{resourceId}.{actionName}';
    const contextResourceId = '{contextResourceId}' + (urlInfo.resource.uiResourceName ? '.' + urlInfo.resource.uiResourceName : '');

    let urlParams: LMUrlParams = {};

    urlParams[urlInfo.resource.resourceType] = contextResourceId;

    urlParams = { ...urlParams, ...this.lmContextService.getContext().contextSessionKeys };
    if (urlInfo.contextSessionKeys) {
      urlParams = { ...urlParams, ...urlInfo.contextSessionKeys };
    }

    return LMUrlUtilService.buildUrl2(urlInfo.basePath, resourceType, resourceId, null, urlParams);
  }

  public buildPreviewActionUrl(urlInfo: LMBaseUrlInfo): string {
    let urlParams: LMUrlParams = { filter: '{filter}' };

    urlParams = { ...urlParams, ...this.lmContextService.getContext().contextSessionKeys } as LMUrlParams;
    if (urlInfo.contextSessionKeys) {
      urlParams = { ...urlParams, ...urlInfo.contextSessionKeys };
    }

    return LMUrlUtilService.buildUrl2(urlInfo.basePath, 'list', '{resourceId}.{listName}', null, urlParams);
  }

  public buildNavigationUrl(urlInfo: LMBaseUrlInfo) {
    const resourceType: LMResourceType = 'navigation';
    const resourceId = '{resourceId}.{navigationName}';

    let urlParams: LMUrlParams = { ...this.lmContextService.getContext().contextSessionKeys } as LMUrlParams;
    if (urlInfo.contextSessionKeys) {
      urlParams = { ...urlParams, ...urlInfo.contextSessionKeys };
    }

    // if (urlInfo.sourceResource != null
    // && (urlInfo.sourceResource.resourceType === 'relation'
    //  || urlInfo.sourceResource.resourceType === 'navigation'
    //  || urlInfo.sourceResource.resourceType === 'drill')) {
    //   // typically for paging when navigating to a form.
    //   urlParams[urlInfo.sourceResource.resourceType] = urlInfo.sourceResource.resourceId;
    // }

    return LMUrlUtilService.buildUrl2(urlInfo.basePath, resourceType, resourceId, null, urlParams);
  }

  public buildRelationActionUrl(urlInfo: LMBaseUrlInfo): string {
    let urlParams = { action: '{actionName}' };

    urlParams = { ...urlParams, ...this.lmContextService.getContext().contextSessionKeys };
    if (urlInfo.contextSessionKeys) {
      urlParams = { ...urlParams, ...urlInfo.contextSessionKeys };
    }

    return LMUrlUtilService.buildUrl2(urlInfo.basePath, 'form', '{resourceId}.{formName}', null, urlParams);
  }

  public buildFormRequestUrl(urlInfo: LMBaseUrlInfo, formSpec?: LMFormSpec): string {
    let urlParams: LMUrlParams = { ...this.lmContextService.getContext().contextSessionKeys } as LMUrlParams;
    if (urlInfo.contextSessionKeys) {
      urlParams = { ...urlParams, ...urlInfo.contextSessionKeys };
    }

    let formResourceId;
    // for cartview the resourceId has not data in it.
    // the data is reqtrieved via a relation using a context
    // like (actor
    if (formSpec?.busFormSpec.implementsCartView) {
      formResourceId = formSpec.resourceId;
    } else {
      formResourceId = '{resourceId}';
    }

    return LMUrlUtilService.buildUrl2(urlInfo.basePath, 'form', formResourceId, null, urlParams);
  }

  public buildHierarchyChildrenUrl(urlInfo: LMBaseUrlInfo): string {
    return this.buildTemplateUrl(urlInfo, urlInfo.resource as KeyedResource, null, { pageop: 'children' }, false);
  }

  public buildHierarchyCenteredUrl(urlInfo: LMBaseUrlInfo): string {
    return this.buildTemplateUrl(urlInfo, urlInfo.resource as KeyedResource, null, { pageop: 'centered' }, false);
  }

  public buildActionRedirectUrl(urlInfo: LMBaseUrlInfo, isTemplateList?: boolean, excludeContextResourceId?: boolean): string {
    let urlParams: LMUrlParams = { output: 'spec' };

    // Exclude contextResourceId if coming from a related create action
    if (!excludeContextResourceId) {
      urlParams[urlInfo.resource.resourceType] = '{contextResourceId}' + (urlInfo.resource.uiResourceName ? '.' + urlInfo.resource.uiResourceName : '');
    }

    urlParams = { ...urlParams, ...this.lmContextService.getContext().contextSessionKeys } as LMUrlParams;
    if (urlInfo.contextSessionKeys) {
      urlParams = { ...urlParams, ...urlInfo.contextSessionKeys };
    }

    if (urlInfo.sourceResource && urlInfo.sourceResource.resourceType === 'list') {
      urlParams[urlInfo.sourceResource.resourceType] = urlInfo.sourceResource.generateResourceId(true);
    }

    if (isTemplateList) {
      urlParams.isTemplateList = true;
    }

    return LMUrlUtilService.buildUrl2(urlInfo.basePath, 'action', '{resourceId}.{actionName}', null, urlParams);
  }

  public buildReportPersonalizationUrl(urlInfo: LMListUrlInfo): string {
    const resourceId = (urlInfo.resource as KeyedResource).generateResourceId(true);

    let urlParams: LMUrlParams = { ...this.lmContextService.getContext().contextSessionKeys } as LMUrlParams;
    if (urlInfo.contextSessionKeys) {
      urlParams = { ...urlParams, ...urlInfo.contextSessionKeys };
    }

    return LMUrlUtilService.buildUrl2(urlInfo.basePath, 'config', resourceId, null, urlParams);
  }

  public buildListPersonalizationUrl(urlInfo: LMListUrlInfo, listSpec: LMListSpec): string {
    let urlParams: LMUrlParams = {};
    urlParams['resources'] = 'ListPersonalization.html';

    if (listSpec.userFolder) {
      urlParams.userfolder = listSpec.userFolder;
    }

    urlParams = { ...urlParams, ...this.lmContextService.getContext().contextSessionKeys } as LMUrlParams;
    if (urlInfo.contextSessionKeys) {
      urlParams = { ...urlParams, ...urlInfo.contextSessionKeys };
    }

    const webAppName = this.getUrlPathValue(urlInfo.basePath, AppConstants.URLSEG_WEBAPP_INDEX, urlInfo.isGenUrl);

    // let webAppName = '';
    // const isGenUrl: boolean = this.isGenUrlUsingWindowLocation();
    // if (urlInfo.url) {
    //   webAppName = this.getUrlPathValue(urlInfo.url.pathname, AppConstants.URLSEG_WEBAPP_INDEX, isGenUrl);
    // } else {
    //   webAppName = this.getUrlPathValue(urlInfo.basePath, AppConstants.URLSEG_WEBAPP_INDEX, isGenUrl);
    // }

    // const isGenUrl: boolean = this.isGenUrlUsingWindowLocation();
    // const webAppName = this.getUrlPathValue(window.location.pathname, AppConstants.URLSEG_WEBAPP_INDEX, isGenUrl);

    return LMUrlUtilService.buildUrl2(urlInfo.basePath, 'static', webAppName, null, urlParams);
  }

  public buildFieldLookupUrl(urlInfo: LMListUrlInfo): string {
    let urlParams: LMUrlParams = { ...this.lmContextService.getContext().contextSessionKeys } as LMUrlParams;
    if (urlInfo.contextSessionKeys) {
      urlParams = { ...urlParams, ...urlInfo.contextSessionKeys };
    }
    return LMUrlUtilService.buildUrl2(urlInfo.basePath, 'list', '{resourceId}', null, urlParams);
  }

  public buildFieldSearchUrl(urlInfo: LMListUrlInfo): string {
    let urlParams: LMUrlParams = { ...this.lmContextService.getContext().contextSessionKeys } as LMUrlParams;
    if (urlInfo.contextSessionKeys) {
      urlParams = { ...urlParams, ...urlInfo.contextSessionKeys };
    }
    return LMUrlUtilService.buildUrl2(urlInfo.basePath, 'search', '{resourceId}', null, urlParams);
  }

  public buildGenAIPromptUrl(urlInfo: LMListUrlInfo): string {
    let urlParams: LMUrlParams = { ...this.lmContextService.getContext().contextSessionKeys } as LMUrlParams;
    if (urlInfo.contextSessionKeys) {
      urlParams = { ...urlParams, ...urlInfo.contextSessionKeys };
    }
    return LMUrlUtilService.buildUrl2(urlInfo.basePath, 'list', '{resourceId}', null, urlParams);
  }

  public buildActorGroupSelectUrl(urlInfo: LMListUrlInfo) {
    const resourceType: LMResourceType = 'list';
    const resourceId = 'ActorGroup.ActorGroupHierarchyList';
    let urlParams: LMUrlParams = {
      type: 'select',
      output: 'spec',
      select: 'ActorGroup.ActorGroup',
    };

    urlParams = { ...urlParams, ...this.lmContextService.getContext().contextSessionKeys } as LMUrlParams;
    if (urlInfo.contextSessionKeys) {
      urlParams = { ...urlParams, ...urlInfo.contextSessionKeys };
    }

    return LMUrlUtilService.buildUrl2(urlInfo.basePath, resourceType, resourceId, null, urlParams);
  }

  public buildRoleSelectUrl(urlInfo: LMListUrlInfo) {
    const resourceType: LMResourceType = 'list';
    const resourceId = 'Role.RoleList';
    let urlParams: LMUrlParams = {
      type: 'select',
      output: 'spec',
      select: 'Role.Role',
    };

    urlParams = { ...urlParams, ...this.lmContextService.getContext().contextSessionKeys } as LMUrlParams;
    if (urlInfo.contextSessionKeys) {
      urlParams = { ...urlParams, ...urlInfo.contextSessionKeys };
    }

    return LMUrlUtilService.buildUrl2(urlInfo.basePath, resourceType, resourceId, null, urlParams);
  }
  public buildRoleGroupSelectUrl(urlInfo: LMListUrlInfo) {
    const resourceType: LMResourceType = 'list';
    const resourceId = 'RoleGroup.RoleGroupList';
    let urlParams: LMUrlParams = {
      type: 'select',
      output: 'spec',
      select: 'RoleGroup.RoleGroup',
    };

    urlParams = { ...urlParams, ...this.lmContextService.getContext().contextSessionKeys } as LMUrlParams;
    if (urlInfo.contextSessionKeys) {
      urlParams = { ...urlParams, ...urlInfo.contextSessionKeys };
    }

    return LMUrlUtilService.buildUrl2(urlInfo.basePath, resourceType, resourceId, null, urlParams);
  }

  public buildRelatedReportSourceUrl(urlInfo: LMListUrlInfo) {
    const resourceType: LMResourceType = 'report';
    const resourceId = (urlInfo.resource as KeyedResource).generateResourceId(true);

    let urlParams: LMUrlParams = {};
    if (urlInfo.urlParams) {
      const userFolder = urlInfo.urlParams['userfolder'];
      if (userFolder) {
        urlParams.userfolder = userFolder;
      }
    }

    urlParams = { ...urlParams, ...this.lmContextService.getContext().contextSessionKeys } as LMUrlParams;
    if (urlInfo.contextSessionKeys) {
      urlParams = { ...urlParams, ...urlInfo.contextSessionKeys };
    }

    return LMUrlUtilService.buildUrl2(urlInfo.basePath, resourceType, resourceId, null, urlParams);
  }

  // public buildSortUrl(urlInfo: LMListUrlInfo, listSpec: LMListSpec, sortOrderName: string, isAscending: boolean): string {
  //   const resourceType: LMResourceType = 'list';
  //   const resourceId = (urlInfo.resource as KeyedResource).generateResourceId(true);
  //
  //   let urlParams: LMUrlParams = {
  //     pageop: 'sort',
  //     pagesize: listSpec.pageSize,
  //     ...this.lmContextService.getContext().contextSessionKeys
  //   };
  //
  //   if (listSpec.pageSize !== AppConstants.INFINITE_PAGESIZE) {
  //     urlParams.sortOrderName = sortOrderName;
  //     urlParams.isAscending = isAscending;
  //   }
  //
  //   return LMUrlUtilService.buildUrl2(urlInfo.basePath, resourceType, resourceId, undefined, urlParams);
  // }

  public buildRelatedButtonActionUrl(urlInfo: LMBaseUrlInfo, formButton: LMButtonLayout): string {
    if (!formButton.relation) {
      return null; // todo throw exception instead?
    }

    // https://usspnppatton01.infor.com:8082/uitest/ConfigurationConsole/action/ConfigEntity.Edit?relation={resourceId}.ConfigEntityRel&output=spec&csk.cli=true

    const actionResource: ActionResource = new ActionResource({
      resourceId: formButton.relBusClass,
      actionName: formButton.name
    });

    let urlParams: LMUrlParams = {
      output: 'spec',
      relation: '{resourceId}.' + formButton.relation,
    };

    urlParams = { ...urlParams, ...this.lmContextService.getContext().contextSessionKeys };

    // -----------------------------------------------------------------------------------
    // workaround for now... until we understand these things:
    //  - where are the extra csks from mingle and custom url callback coming from
    //    and how to mitigate them.
    // -----------------------------------------------------------------------------------
    if (urlInfo.contextSessionKeys) {
      urlParams = { ...urlParams, ...urlInfo.contextSessionKeys };
    }

    return LMUrlUtilService.buildUrl2(urlInfo.basePath, actionResource.resourceType, actionResource.resourceId, null, urlParams);
  }

  /**
   * Used to build a related action url
   */
  public buildRelatedMenuActionUrl(urlInfo: LMBaseUrlInfo, actionReference: LMBusActionReference | LMFormButtonSpec): string {
    if (!actionReference.relation) {
      return null; // todo throw exception instead?
    }

    const actionResource: ActionResource = new ActionResource({
      resourceId: actionReference.relBusClass,
      actionName: actionReference.name
    });

    let urlParams: LMUrlParams = {
      output: 'spec',
      relation: '{resourceId}.' + actionReference.relation,
    };

    urlParams = { ...urlParams, ...this.lmContextService.getContext().contextSessionKeys };

    // -----------------------------------------------------------------------------------
    // workaround for now... until we understand these things:
    //  - find out where the extra csks from mingle and custom url callbacks are coming
    //    from and mitigate them.
    // -----------------------------------------------------------------------------------
    if (urlInfo.contextSessionKeys) {
      urlParams = { ...urlParams, ...urlInfo.contextSessionKeys };
    }

    return LMUrlUtilService.buildUrl2(urlInfo.basePath, actionResource.resourceType, actionResource.resourceId, null, urlParams);
  }

  public buildBusinessTaskUrl(urlInfo: LMBaseUrlInfo, actionReference: LMBusActionReference): string {
    if (!actionReference.relBusTask) {
      return null;
    }
    const keyedResource = new KeyedResource({ resourceId: actionReference.relBusTask + '.' + actionReference.relBusTaskForm});
    let urlParams: LMUrlParams = {
      output: 'spec',
      action: actionReference.name,
    };
    urlParams = { ...urlParams, ...this.lmContextService.getContext().contextSessionKeys };
    if (urlInfo.contextSessionKeys) {
      urlParams = { ...urlParams, ...urlInfo.contextSessionKeys };
    }
    return LMUrlUtilService.buildUrl2(urlInfo.basePath, ((actionReference.relBusTaskDlg || actionReference.willRunAsynchronous) ? 'form' : 'action'), keyedResource.resourceId, null, urlParams);
  }

  public buildSelectListUrl(urlInfo: LMBaseUrlInfo, selectListInfo: LMSelectListInfo): string {
    let urlParams: LMUrlParams = { output: 'spec', type: 'select', };
    urlParams[selectListInfo.selectResourceType] = selectListInfo.selectResourceId;
    if (selectListInfo.selectLink) {
      urlParams['selectlink'] = selectListInfo.selectLink;
    }

    urlParams = { ...urlParams, ...this.lmContextService.getContext().contextSessionKeys };
    if (urlInfo.contextSessionKeys) {
      urlParams = { ...urlParams, ...urlInfo.contextSessionKeys };
    }

    return LMUrlUtilService.buildUrl2(urlInfo.basePath, 'list', selectListInfo.selectListResourceId, null, urlParams);
  }

  public buildTemplateUrl(
    urlInfo: LMBaseUrlInfo,
    resource: KeyedResource,
    sourceResource?: KeyedResource,
    urlParams?: LMUrlParams,
    isDependentForm?: boolean,
    urlParamsToBeEncode?: LMUrlParams
  ): string {
    if (!urlParams) {
      urlParams = {};
    }

    if (!urlParamsToBeEncode) {
      urlParamsToBeEncode = {};
    }

    if (sourceResource != null && sourceResource.resourceType !== AppConstants.RESOURCETYPE_FORMINVOKE) {
      // ------------------------------------------------------------------------------------------------
      // Exclude the sourceResourceId url param when it's a form invoke. Form invoke source resourceid
      // is only good for the formInvokeCreateUrl when creating a record navigated to via a formInvoke
      // ------------------------------------------------------------------------------------------------
      if (isDependentForm) {
        // In the case of dependent form like detail section form, the source resource Id will depend on the list selection.
        const sourceResourceId = '{sourceResourceId}' + (sourceResource.uiResourceName ? '.' + sourceResource.uiResourceName : '');
        urlParams[sourceResource.resourceType] = sourceResourceId;
      } else {
        urlParams[sourceResource.resourceType] = sourceResource.generateResourceId(true);
      }
    }

    // ------------------------------------------------------------------------------------------------
    // propagate context session keys (csks).
    // ------------------------------------------------------------------------------------------------
    urlParams = { ...urlParams, ...this.lmContextService.getContext().contextSessionKeys };
    if (urlInfo.contextSessionKeys) {
      urlParams = { ...urlParams, ...urlInfo.contextSessionKeys };
    }

    // ------------------------------------------------------------------------------------------------
    // build the resourceId template.
    // ------------------------------------------------------------------------------------------------
    const templateResourceId: string = '{resourceId}' + (resource.uiResourceName ? '.' + resource.uiResourceName : '');

    // ------------------------------------------------------------------------------------------------
    // build the new data url
    // ------------------------------------------------------------------------------------------------
    return LMUrlUtilService.buildUrl2(urlInfo.basePath, resource.resourceType, templateResourceId, urlParamsToBeEncode, urlParams);
  }

  /**
   * @param basePath - the url up until, but not including, the resourceType (list, form, page, etc...)
   * @param resourceType - the resourceType of the resource to buildthe url path
   * @param resourceId - the resourceId used to build the url path
   * @param urlParamsToEncode?? pass in map object here if you want encoding - useful for browser address urls
   * @param urlParams?? pass in map object here if you don't want url encoding - better for ajax urls so they are more human readable
   * @return the url
   */
  public static buildUrl2(
    basePath: string,
    resourceType: string,
    resourceId: string,
    urlParamsToEncode?: LMUrlParams,
    urlParams?: LMUrlParams
  ): string {
    let url: string = basePath + '/' + resourceType + '/' + resourceId;
    let delimiter = '?';

    if (urlParamsToEncode != null && !isEmpty(urlParamsToEncode)) {
      url += delimiter + Object.entries(urlParamsToEncode).map(pair => LMUrlUtilService.toUrlParam(pair, true)).join('&');
      delimiter = '&';
    }

    if (urlParams != null && !isEmpty(urlParams)) {
      url += delimiter + Object.entries(urlParams).map(pair => LMUrlUtilService.toUrlParam(pair)).join('&');
      delimiter = '&';
    }

    return url;
  }

  public buildUserListViewFetchUrl(urlInfo: LMBaseUrlInfo, listSpec: LMListSpec) {
    let urlParams = { ...this.lmContextService.getContext().contextSessionKeys } as any;
    if (urlInfo.contextSessionKeys) {
      urlParams = { ...urlParams, ...urlInfo.contextSessionKeys };
    }

    const urlParamsToBeEncoded: LMUrlParams = {};
    if (listSpec.userFolder) {
      urlParamsToBeEncoded.userfolder = listSpec.userFolder;
    }

    return LMUrlUtilService.buildUrl2(
      urlInfo.basePath,
      'config',
      urlInfo.resource.generateResourceId(true),
      urlParamsToBeEncoded,
      urlParams
    );
  }

  public static toUrlParam(pair: [ string, (string | string[]) ], encode?: boolean): string {
    if (pair.length !== 2) {
      // eslint-disable-next-line no-throw-literal
      throw 'toUrlParam: invalid args: expected array of [ string, (string | string[]) ] but got ' + pair;
    }

    const key = pair[0];
    if (pair[1] instanceof Array) {
      return (pair[1] as string[]).map(value2 =>
        LMUrlUtilService.toUrlParam([ key, value2 ], encode)).join('&');
    }

    const value = encode ? encodeURIComponent(pair[1]) : pair[1];
    return key + '=' + value;
  }

  /**
   * todo ppwatton move these functions that don't have any dependencies to a new service (name it
   *  lm-spec-util.service.ts) to be so it can be used w/o the problems of circular dependencies.
   *  NOTE: this function is also defined in lm-action.service.ts due to a circular dependency.
   */
  public getActionByName(actions: Array<LMActionSpec>, actionName: string): LMActionSpec {
    if (!actions || !actionName) {
      return null;
    }
    return actions.find(findActionSpec => findActionSpec.name === actionName);
  }

  public isPageUrl(href: string): boolean {
    return this.getResourceTypeFromUrl(href) === AppConstants.RESOURCETYPE_PAGE;
  }

  public getResourceTypeFromUrl(href: string): string {
    const isClientUrlAbsolute = this.isAbsoluteUrl(href);
    const url: URL = new URL(href, !isClientUrlAbsolute ? window.location.origin : undefined);
    const isGenUrl: boolean = this.isGenUrlUsingHref(href);
    return this.getUrlPathValue(url.pathname, AppConstants.URLSEG_RESOURCE_TYPE_INDEX, isGenUrl);
  }

  public getUrlPathArray(urlPath: string): string[] {
    const pathArray: string[] = urlPath.split('/');
    if (pathArray[0].trim().length === 0) {
      // first char of / causes empty first element. remove empty first element.
      pathArray.shift();
    }
    return pathArray;
  }

  /**
   * @param urlPath
   * @param index - valid args:
   *   AppConstants.URLSEG_PDL_OR_DA_INDEX,
   *   AppConstants.URLSEG_WEBAPP_INDEX,
   *   AppConstants.URLSEG_RESOURCE_TYPE_INDEX
   *   AppConstants.URLSEG_RESOURCE_ID_INDEX
   * @param isGenUrl
   */
  public getUrlPathValue(urlPath: string, index: number, isGenUrl?: boolean) {
    const pathArray: string[] = this.getUrlPathArray(urlPath);

    // if (isGenUrl || this.lmContextService.isGenProductLine()) {
    if (isGenUrl) {
      return pathArray[index - 1];
    }

    return pathArray[index];
  }

  /**
   * @deprecated use isGenUrlUsingHref instead.
   * @param url
   */
  public isGenUrlUsingURL(url: URL): boolean {
    if (!url) {
      return false;
    }
    const genParam: string = url.searchParams.get('csk.gen');
    // tslint:disable-line:triple-equals - want to be forgiving of case
    // eslint-disable-next-line eqeqeq
    return genParam == 'true';
  }

  public isGenUrlUsingHref(url: string): boolean {
    if (!url) {
      return false;
    }

    const paramValue = this.getUrlParam(url, 'csk.gen');
    if (!paramValue || paramValue.trim().length === 0) {
      return false;
    }

    // tslint:disable-line:triple-equals - want to be forgiving of case
    // eslint-disable-next-line eqeqeq
    return paramValue == 'true';
  }

  public isGenUrlUsingWindowLocation(): boolean {
    const genParam: string = this.getUrlParam(window.location.href, 'csk.gen');
    // tslint:disable-line:triple-equals - want to be forgiving of case
    // eslint-disable-next-line eqeqeq
    return genParam == 'true';
  }

  public allCsksMatchContext(currentPageUrl: string): boolean {
    const cskParams: Params = this.getPrefixedParams(currentPageUrl, 'csk.');
    const urlCskArray: string[] = Object.keys(cskParams).map(key => `${key}=${cskParams[key]}`);

    const contextSessionKeys = this.lmContextService.getContext()?.contextSessionKeys || {};
    const ctxCskArray: string[] = Object.keys(contextSessionKeys).map(key => `${key}=${contextSessionKeys[key]}`);

    return urlCskArray.sort().join() === ctxCskArray.sort().join();
  }

  public buildURLFromHref(url: string): URL {
    if (!url) {
      return null;
    }

    const isUrlAbsolute = this.isAbsoluteUrl(url);
    return new URL(url, !isUrlAbsolute ? window.location.origin : undefined);
  }

  // private buildNavigationContextParams(navigationContext: LMNavContextKeys) {
  //   const params: Params = {};
  //
  //   Object.keys(navigationContext).forEach(key => {
  //     if (key.startsWith('nc.')) {
  //       params[key] = navigationContext[key];
  //     }
  //   });
  //
  //   return params;
  // }

  public getMatrixDateFilterUrl(formUrlInfo: LMFormUrlInfo, resourceId: string, date: string, dateFilterFieldId: string): string {
    const dateFilterFieldParam = this.getDateFilterFieldParam(date, dateFilterFieldId);
    const urlCompiledTemplate = urltemplate.parse(formUrlInfo.initialDataUrl.split('?')[0]);
    const sourceResource = formUrlInfo.sourceResource.resourceId;
    const sourceResourceType = formUrlInfo.sourceResource.resourceType;
    const url = urlCompiledTemplate.expand({ resourceId });


    return `${url}?pageop=filter&${sourceResourceType}=${sourceResource}&dateFilterField=${dateFilterFieldParam}`;
  }

  private getDateFilterFieldParam(date: string, dateFilterFieldId: string): string {
    const dateParts = date.split('/');
    const year = dateParts[1];
    const month = dateParts[0].length === 1 ? `0${dateParts[0]}` : dateParts[0];

    const numberOfDays = new Date(Number(year), Number(month), 0).getDate();
    const startDate = `${year}${month}01`;
    const endDate = `${year}${month}${numberOfDays}`;

    return `%28${dateFilterFieldId}%20%3E%3D%20%22${startDate}%22%20and%20${dateFilterFieldId}%20%3C%3D%20%22${endDate}%22%29`;
  }

  /**
   * Gets a resource object representing the current route url.
   */
  public getRouteResource(url?: string): KeyedResource {

    let resourceType;
    let resourceId;
    if (url) {
      const isGenUrl = this.isGenUrlUsingHref(url);
      const urlObject = this.buildURLFromHref(url);
      resourceType = this.getUrlPathValue(urlObject.pathname, AppConstants.URLSEG_RESOURCE_TYPE_INDEX, isGenUrl);
      resourceId = this.getUrlPathValue(urlObject.pathname, AppConstants.URLSEG_RESOURCE_ID_INDEX, isGenUrl);
    } else {
      const isGenUrl = this.lmContextService.isGenProductLine();
      resourceType = this.getUrlPathValue(window.location.pathname, AppConstants.URLSEG_RESOURCE_TYPE_INDEX, isGenUrl);
      resourceId = this.getUrlPathValue(window.location.pathname, AppConstants.URLSEG_RESOURCE_ID_INDEX, isGenUrl);
    }
    const resource: KeyedResource = new KeyedResource({ resourceType, resourceId });
    return resource;
  }

  public copyCubeParamsForDetailSection(listUrlInfo: LMListUrlInfo, fromDataView: LMDataView): LMListUrlInfo {
    if (fromDataView && fromDataView.cubeInfo) {
      listUrlInfo.cubeParams = {};
      listUrlInfo.cubeParams.cubeMediator = fromDataView.cubeInfo.cubeMediator;
      listUrlInfo.cubeParams.dimFieldIndex = fromDataView.cubeInfo.dimFieldIndex;
      if (fromDataView.cubeInfo.pobdIdx !== undefined) {
        listUrlInfo.cubeParams.pobdIdx = fromDataView.cubeInfo.pobdIdx;
      }
      listUrlInfo.cubeParams.isFactRowElement = fromDataView.cubeInfo.isFactRowElement;
      listUrlInfo.cubeParams.dimensions = fromDataView.cubeInfo.dimensions;
      if (fromDataView.cubeInfo.ctxDimensions) {
        listUrlInfo.cubeParams.ctxDimensions = fromDataView.cubeInfo.ctxDimensions;
      }
      if (fromDataView.cubeInfo.fltrDimensions) {
        listUrlInfo.cubeParams.fltrDimensions = fromDataView.cubeInfo.fltrDimensions;
      }

      if (fromDataView.cubeInfo.extraInfo) {
        if (fromDataView.cubeInfo.extraInfo.periodView) {
          listUrlInfo.cubeParams.periodView = fromDataView.cubeInfo.extraInfo.periodView;
          listUrlInfo.cubeParams.pvPeriodIdx = fromDataView.cubeInfo.extraInfo.pvPeriodIdx;
        }
        if (fromDataView.cubeInfo.extraInfo.pivotView) {
          listUrlInfo.cubeParams.pivotView = fromDataView.cubeInfo.extraInfo.pivotView;
        }
        if (fromDataView.cubeInfo.extraInfo.columnView) {
          listUrlInfo.cubeParams.columnView = fromDataView.cubeInfo.extraInfo.columnView;
        }
        listUrlInfo.cubeParams.cubeNm = fromDataView.cubeInfo.extraInfo.cubeNm;
        listUrlInfo.cubeParams.periodKey = fromDataView.cubeInfo.extraInfo.periodKey;
        listUrlInfo.cubeParams.reportingBasis = fromDataView.cubeInfo.extraInfo.reportingBasis;
        listUrlInfo.cubeParams.topNodeDims = fromDataView.cubeInfo.extraInfo.topNodeDims;
        listUrlInfo.cubeParams.topContextFields = fromDataView.cubeInfo.extraInfo.topContextFields;
      }
    }
    return listUrlInfo;
  }

  public hasSameParamValue(currentPageUrl: string, initialDataUrl: string, paramName: string): boolean {
    const currentPageFilter = this.getUrlParam(currentPageUrl, paramName);
    const initialDataUrlFilter = this.getUrlParam(initialDataUrl, paramName);

    // (null === null) return false!
    if (!currentPageFilter && !initialDataUrlFilter) {
      return true;
    }

    return (currentPageFilter === initialDataUrlFilter);
  }

  public buildSavePersonalizationUrl(urlInfo: LMBaseUrlInfo, listSpec: LMListSpec) {
    let urlParams = { ...this.lmContextService.getContext().contextSessionKeys } as any;
    if (urlInfo.contextSessionKeys) {
      urlParams = { ...urlParams, ...urlInfo.contextSessionKeys };
    }

    const urlParamsToBeEncoded: LMUrlParams = {};
    if (listSpec.userFolder) {
      urlParamsToBeEncoded.userfolder = listSpec.userFolder;
    }

    return LMUrlUtilService.buildUrl2(
      urlInfo.basePath,
      urlInfo.resource.resourceType,
      urlInfo.resource.resourceId,
      urlParamsToBeEncoded,
      urlParams
    );
  }

  public addDataContextUrlParams(urlParams: LMUrlParams): LMUrlParams {
    if (!this.lmContextService.useNewSpecUrls() && this.lmContextService.getContext()) {
      urlParams[AppConstants.PARAM_CSK_ASOFDATE] = this.lmContextService.getAsOfDate();

      if (this.lmContextService.getShowDeletedRecords()) {
        urlParams[AppConstants.PARAM_CSK_SHOWDELETEDRECORDS] = true;
      }

      if (this.lmContextService.isArchiveMode()) {
        urlParams[AppConstants.PARAM_CSK_VIEWARCHIVE] = true;
      } else {
        //@Sarah: Not sure why we need to reload menu when exiting asofdate and showdeletedrecord.
        // This change alone fixed the issue reported in LMWEB-21387 
        // because unnecessary csks [asofdate/archive] will be removed when the menu item was clicked and goes here.
        // If we reload menu like asofdate and showdeletedrecords, and webappmenuspec was saved, we will reuse that
        // that means that if we exit asofdate mode then the url in the menu still has csk.asofdate
        // also, if we right click a menu item and and open it in a new tab, the tab will have the banner button
        // TODO: Need to check how webAppMenuSpec can be null/undefined OR how webAppName can be not present in webAppSpec so we can check the other scenarios
        urlParams[AppConstants.PARAM_CSK_VIEWARCHIVE] = undefined;
      }
    }

    return urlParams;
  }

  public getUrlPathValues(url: string, isGenUrl = false): LMPathValues {

    url = this.toRelativeUrl(url);
    url = window.location.origin+ url;

    const urlObject = new URL(url);
    const pdlOrDa = this.getUrlPathValue(urlObject.pathname, AppConstants.URLSEG_PDL_OR_DA_INDEX, isGenUrl);
    const webApp = this.getUrlPathValue(urlObject.pathname, AppConstants.URLSEG_WEBAPP_INDEX, isGenUrl);
    const resourceType = this.getUrlPathValue(urlObject.pathname, AppConstants.URLSEG_RESOURCE_TYPE_INDEX, isGenUrl);
    const resourceId = this.getUrlPathValue(urlObject.pathname, AppConstants.URLSEG_RESOURCE_ID_INDEX, isGenUrl);

    const resourceParts = resourceId.split('.');
    const cleanResourcePartOne = decodeURIComponent(resourceParts[0]);
    const keysLoc = cleanResourcePartOne.indexOf('(');
    const busClassName = keysLoc > -1 ? cleanResourcePartOne.substring(0,keysLoc): cleanResourcePartOne;
    const keys = cleanResourcePartOne.indexOf('(') > -1 ? cleanResourcePartOne.substring(keysLoc) : '';
    const name = resourceParts[1] ? resourceParts[1] : '';

    return { pdlOrDa, webApp, resourceType, resourceId, busClassName, name, keys };
  }

  urlHostMatchesWindowLocationHost(url: string): boolean {
    const urlObject = this.buildURLFromHref(url);
    if (!urlObject) {
      return false;
    }

    return urlObject.host === window.location.host;
  }
}
